import { ClipboardCheck, Target, CalendarCheck, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';

export const HowItWorks = () => {
  const steps = [
    {
      icon: ClipboardCheck,
      step: '01',
      title: 'Tell Us Your Goals',
      description: 'Share your service areas, ideal customer profile, and appointment volume targets. We customize our approach to your specific business needs.',
    },
    {
      icon: Target,
      step: '02',
      title: 'We Target Homeowners',
      description: 'Our marketing experts create targeted campaigns to reach homeowners actively searching for your services in your exclusive territory.',
    },
    {
      icon: CalendarCheck,
      step: '03',
      title: 'Qualified Appointments',
      description: 'We pre-qualify leads and schedule appointments directly on your calendar. You only receive homeowners ready to discuss their projects.',
    },
    {
      icon: TrendingUp,
      step: '04',
      title: 'Close & Grow',
      description: 'Focus on what you do best — closing deals and delivering excellent work. We handle the marketing so you can scale your business.',
    },
  ];

  return (
    <section className="py-20 md:py-32" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Section Header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-end mb-16">
          <div>
            <p className="text-sm tracking-wide uppercase font-semibold text-[#2563eb] mb-4">
              How It Works
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0f172a] tracking-tight">
              From First Contact to Booked Appointment
            </h2>
          </div>
          <div>
            <p className="text-lg text-[#64748b] leading-relaxed">
              Our proven 4-step process ensures you receive only high-quality appointments 
              with homeowners who are ready to move forward with their projects.
            </p>
          </div>
        </div>

        {/* Steps Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => (
            <div
              key={step.step}
              data-testid={`step-${index + 1}`}
              className="relative group"
            >
              {/* Connector Line */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-8 left-full w-full h-px bg-[#e2e8f0] z-0" />
              )}
              
              <div className="relative bg-white p-8 border border-[#e2e8f0] hover:border-[#2563eb]/30 rounded-sm transition-all duration-300 group-hover:shadow-[0_12px_24px_rgba(15,23,42,0.08)]">
                {/* Step Number */}
                <div className="absolute -top-4 -right-4 w-12 h-12 bg-[#0f172a] text-white rounded-full flex items-center justify-center text-sm font-bold">
                  {step.step}
                </div>
                
                {/* Icon */}
                <div className="w-16 h-16 bg-[#2563eb]/10 rounded-sm flex items-center justify-center mb-6 group-hover:bg-[#2563eb]/20 transition-colors duration-300">
                  <step.icon className="w-8 h-8 text-[#2563eb]" />
                </div>
                
                {/* Content */}
                <h3 className="text-xl font-bold text-[#0f172a] mb-3">{step.title}</h3>
                <p className="text-[#64748b] leading-relaxed">{step.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <Link to="/contact">
            <Button 
              className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-semibold px-8 h-14 rounded-sm text-base shadow-[0_4px_14px_0_rgba(37,99,235,0.39)] hover:shadow-[0_6px_20px_0_rgba(37,99,235,0.5)] transition-all duration-200 hover:-translate-y-0.5"
              data-testid="how-it-works-cta"
            >
              Start Getting Appointments Today
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};
